import React, { useEffect, useRef, useState, useContext } from "react";
import { useKeycloak } from "@react-keycloak/web";
import SessionExpiryModal from "./SessionExpiryModal";
import ValueContext from "../../ContextAPi/appContext";
import { redirectUri } from "../../services/axiosConfigCS";

const USER_TOKEN_KEY = "userToken";
const WARNING_WINDOW_SEC = 120;
const CHECK_INTERVAL_MS = 10000;

function decodeJwtPayload(token) {
  try {
    let base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4) base64 += "=";
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function getTokenRemainingSec(keycloak) {
  const token = keycloak?.token || localStorage.getItem(USER_TOKEN_KEY);
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return null;
  return Math.floor(payload.exp - Date.now() / 1000);
}

// Store session expired state in localStorage and refresh token expiry time
const storeSessionExpiredState = (keycloak) => {
  const refreshPayload = decodeJwtPayload(
    keycloak?.refreshToken
  );

  const data = {
    expiredAt: new Date().toISOString(),
    refreshTokenExp: refreshPayload?.exp || null
  };

  localStorage.setItem(
    "sessionExpired",
    JSON.stringify(data)
  );
};


function syncKeycloakFromStoredAccessToken(keycloak) {
  const token = localStorage.getItem(USER_TOKEN_KEY);
  if (!token || !keycloak) return false;

  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return false;

  keycloak.token = token;
  keycloak.tokenParsed = payload;
  keycloak.authenticated = true;

  return true;
}

const SessionExpiryManager = ({ editorRoute }) => {
  const { keycloak } = useKeycloak();
  const appData = useContext(ValueContext);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [countdown, setCountdown] = useState(0);


  const sessionModalOpenRef = useRef(false);

  const timers = useRef({
    warning: null,
    countdown: null,
    check: null,
  });

  const clearTimers = () => {
    const t = timers.current;
    if (t.warning) clearTimeout(t.warning);
    if (t.countdown) clearInterval(t.countdown);
    if (t.check) clearInterval(t.check);
    timers.current = { warning: null, countdown: null, check: null };
  };
   // New tab after expiry → redirect
  //  useEffect(() => {
  //   const expired = localStorage.getItem("sessionExpired");
  //   if(expired) {
  //     handleLogout();
  //   }
  // }, [keycloak?.authenticated]);
  const handleLogout = () => {
    sessionModalOpenRef.current = false;
    localStorage.removeItem("sessionExpired");
    clearTimers();
    localStorage.removeItem(USER_TOKEN_KEY);
    keycloak.logout({
      redirectUri: redirectUri,
    });
  };

  // 🔴 GLOBAL EXPIRY (single source of truth)
  const applyExpiredUi = () => {
    if (isExpired) return;
    storeSessionExpiredState(keycloak);

    setIsExpired(true);
    sessionModalOpenRef.current = true;
    setIsModalOpen(true);
    setCountdown(0);

    clearTimers(); // stop all timers in this tab
  };

  const startCountdown = (seconds) => {
    clearInterval(timers.current.countdown);

    const endsAt = Date.now() + seconds * 1000;
    setCountdown(seconds);

    let lastValue = null;

timers.current.countdown = setInterval(() => {
  if (localStorage.getItem("sessionExpired")) {
    applyExpiredUi();
    return;
  }

  const remaining = Math.max(
    0,
    Math.floor((endsAt - Date.now()) / 1000)
  );

  // ✅ Prevent unnecessary/skipped renders
  if (remaining !== lastValue) {
    lastValue = remaining;
    setCountdown(remaining);
  }

  if (remaining === 0) {
    clearInterval(timers.current.countdown);
    applyExpiredUi();
  }
}, 300); // 🔥 run faster
  };
  const openWarningWithCountdown = (seconds) => {
    if (sessionModalOpenRef.current) return;

    sessionModalOpenRef.current = true;

    // storeSessionExpiredState(keycloak); commenting this as its causing where the expiry popup countdown is not coming

    if (!appData?.saveCanvasDataOnSessionExpire && editorRoute) {
      appData?.setSaveCanvasDataOnSessionExpire?.(true);
    }

    setIsModalOpen(true);
    setIsExpired(false);
    startCountdown(seconds);
  };

  const setupTimers = () => {
    clearTimers();

    // 🔴 If already expired globally → respect it
    if (localStorage.getItem("sessionExpired")) {
      applyExpiredUi();
      return;
    }

    const remaining = getTokenRemainingSec(keycloak);
    if (remaining == null) return;

    if (remaining <= 0) {
      applyExpiredUi();
      return;
    }

    if (remaining <= WARNING_WINDOW_SEC) {
      openWarningWithCountdown(remaining);
    } else {
      timers.current.warning = setTimeout(() => {
        openWarningWithCountdown(WARNING_WINDOW_SEC);
      }, (remaining - WARNING_WINDOW_SEC) * 1000);
    }

    timers.current.check = setInterval(() => {
      // 🔴 Always trust global expiry first
      if (localStorage.getItem("sessionExpired")) {
        applyExpiredUi();
        return;
      }

      const current = getTokenRemainingSec(keycloak);
      if (current == null) return;

      if (current <= 0) {
        applyExpiredUi();
        return;
      }

      if (current <= WARNING_WINDOW_SEC) {
        openWarningWithCountdown(current);
      }
    }, CHECK_INTERVAL_MS);
  };

  const handleContinue = async () => {
    try {
      sessionModalOpenRef.current = false;
      setIsModalOpen(false);
      await keycloak.updateToken(-1);

      localStorage.setItem(USER_TOKEN_KEY, keycloak.token);
      localStorage.removeItem("sessionExpired"); // 🔥 CLEAR global expiry

      setIsExpired(false);

      clearTimers();
      setupTimers();
    } catch {
      handleLogout();
    }
  };

  // ✅ Setup after login
  useEffect(() => {
    if (!keycloak?.authenticated) return;

    localStorage.removeItem("sessionExpired");
    sessionModalOpenRef.current = false;

    if (keycloak.token) {
      localStorage.setItem(USER_TOKEN_KEY, keycloak.token);
    }

    setupTimers();
    return clearTimers;
  }, [keycloak?.authenticated]);

  //  CROSS TAB SYNC (CRITICAL FIX)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "sessionExpired") {
        setIsExpired(true);
        setIsModalOpen(true);
        setCountdown(0);
        clearTimers();
      }

      if (e.key === "sessionWarning") {
        const remaining = getTokenRemainingSec(keycloak);
        if (remaining > 0) {
          openWarningWithCountdown(
            Math.min(remaining, WARNING_WINDOW_SEC)
          );
        }
      }

      if (e.key === USER_TOKEN_KEY) {
        syncKeycloakFromStoredAccessToken(keycloak);

        setIsModalOpen(false);
        setIsExpired(false);

        clearTimers();
        setupTimers();
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);



  return (
    <SessionExpiryModal
      showModal={isModalOpen}
      expired={isExpired}
      remainingTime={countdown}
      onContinue={handleContinue}
      onLoginAgain={handleLogout}
      onLogOut={handleLogout}
    />
  );
};

export default SessionExpiryManager;